import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Badge, Box, Flex, Hide } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { path } from 'ramda';
import { themeGet } from 'styled-system';

import ToggleContent from '@shared/v2/components/ToggleContent';
import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import { getCampaignEndMessage } from '@shared/v2/utils/getCampaignEndMessage';
import getPackageOptions from '@shared/v2/utils/getPackageOptions';

import Button from '@/components/Button';
import DealInclusions from '@/components/DealInclusions';
import Divider from '@/components/Divider';
import LinkButton from '@/components/LinkButton';
import PackageHeroSummary from '@/components/PackageHeroSummary';
import PricePerTraveller from '@/components/PricePerTraveller';
import PromoPill from '@/components/PromoPill';
import ReadMore from '@/components/ReadMore';
import Skeleton from '@/components/Skeleton';
import Text from '@/components/Text';
import ViewDetailsModal from '@/components/ViewDetailsModal';
import { TILE_TYPE } from '@/constants';
import useCampaignPills from '@/hooks/useCampaignPills';
import useSearchQuery from '@/hooks/useSearchQuery';
import { Tile } from '@/propTypes/Campaign';
import ImageGallery from '@/shared/components/ImageGallery/ImageGallery';
import useSize from '@/shared/hooks/useSize';
import { prependFlightCampaignInclusion } from '@/utils/campaigns';
import getCheapestOriginForProcuredOffer from '@/utils/getCheapestOriginForProcuredOffer';
import getPropertyLink from '@/utils/getPropertyLink';
import {
  checkAvailabilityDataLayerEvent,
  viewAllOptionsDataLayerEvent,
  viewPackageDetailsDataLayerEvent,
} from '@/v2/components/pages/CampaignView/utils/dataLayer';
import AllPackageOptionsModal from '@/v2/components/ui/AllPackageOptionsModal';
import ProcuredOfferFooter from '@/v2/components/ui/ProcuredOffer/components/ProcuredOfferFooter';
import PropertyDestination from '@/v2/components/ui/PropertyTitle/components/PropertyDestination';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { usePackagePageV2Analytics } from '@/v2/hooks/featureFlags/packagePageV2Analytics/usePackagePageV2Analytics';
import useSelectItemEvent from '@/v2/hooks/useSelectItemEvent';
import createFlightCampaignInclusion from '@/v2/utils/campaign/createFlightCampaignInclusion';
import { dataLayer } from '@/v2/utils/dataLayer';
import { getOriginName } from '@/v2/utils/getOriginName';
import PropertyTitle from '../../../../../PropertyTitle';
import withSkeleton from '../../../../../Skeleton/withSkeleton';

const StyledPriceTotal = styled.div`
  color: ${themeGet('colors.primary')};
`;

const StyledPromoPillWrapper = styled.div`
  position: absolute;
  top: -20px;
  z-index: 1;
`;

const PropertyOption = ({
  priceForAdult = false,
  hidePerPersonPrice = false,
  tile,
  flightPromotionCode,
  index,
}) => {
  const shouldUseNewEvents = useGA4Events();
  const { height, ref: sizeRef } = useSize();
  const {
    data: { originCode },
  } = usePackageSearch();
  const { push, query } = useRouter();
  const procuredOfferCheapestOrigin = useMemo(
    () => getCheapestOriginForProcuredOffer(tile.procuredOffer),
    [tile.procuredOffer],
  );
  const { campaignPills } = useCampaignPills();
  const flightCampaign = path([flightPromotionCode], campaignPills);
  const flightPillText = flightCampaign?.flightPillText;
  const flightCampaignTerms = flightCampaign?.termsConditions;

  const shouldUseNewAnalytics = usePackagePageV2Analytics();

  const {
    propertyContent,
    nights,
    cheapestOrigin,
    travellers = 0,
    tagline,
    inclusions,
    routes,
    displayTravellerTypes,
  } = {
    [TILE_TYPE.STANDARD_PROPERTY]: {
      ...tile,
    },
    [TILE_TYPE.FEATURED_PROPERTY]: tile.featuredProperty && {
      ...tile,
      travellers: tile.featuredProperty.travellers,
      nights: tile.featuredProperty.nights,
      cheapestOrigin: tile.featuredProperty.cheapestOrigin,
      tagline: tile.propertyContent.tagline,
      origins: tile.featuredProperty.propertyOrigins,
    },
    [TILE_TYPE.PROCURED_OFFER]: tile.procuredOffer && {
      ...tile,
      travellers: tile.procuredOffer.rooms?.[0].leadInOffers?.[0].travellers,
      inclusions: prependFlightCampaignInclusion(
        tile.procuredOffer.rooms?.[0].leadInOffers?.[0].inclusions,
        flightPillText,
        flightCampaignTerms,
      ),
      nights: tile.procuredOffer.rooms?.[0].leadInOffers?.[0].minNumberOfNights,
      cheapestOrigin: procuredOfferCheapestOrigin,
      routes: tile.procuredOffer.routes,
      displayTravellerTypes:
        tile.procuredOffer.rooms?.[0].leadInOffers?.[0].displayTravellerTypes,
    },
  }[tile.type];
  const hasInclusions = inclusions && inclusions.length > 0;

  // eslint-disable-next-line max-len
  const pillText = tile.procuredOffer
    ? getCampaignEndMessage({
        dateRange: tile.procuredOffer?.saleDateRange.end,
        extended: tile.procuredOffer?.extended,
      })
    : tile.featuredProperty?.pillText;

  const { searchQuery } = useSearchQuery();

  const originPrice =
    routes?.filter((op) => op.origin === originCode)[0] ?? null;

  const {
    origin,
    price: { perAdult, perTraveller, total, valuedAtTotal },
  } = originPrice || cheapestOrigin || { price: {} };

  const departingOrigin = getOriginName(originCode);

  const flightInclusion = createFlightCampaignInclusion({
    title: flightPillText,
    terms: flightCampaignTerms,
  });
  const { packageOptions } = getPackageOptions({
    procuredOffer: tile.procuredOffer,
    originCode,
    nights,
    title: flightPillText ? `${flightPillText} Deal` : undefined,
    commonInclusions: flightInclusion ? [flightInclusion] : undefined,
  });

  const shouldShowViewOtherOptions = packageOptions.length > 1;

  const handleViewAllOptions = (event, show) => {
    event.preventDefault();

    if (shouldUseNewAnalytics) {
      viewAllOptionsDataLayerEvent();
    }
    show();
  };

  const href = getPropertyLink({
    propertyContent,
    searchQuery: {
      ...searchQuery,
      originCode: originCode || origin?.code,
    },
  });

  const selectItemEventData = useSelectItemEvent({
    originCode,
    property: tile,
    destination: tile.propertyContent?.destination?.title,
    name: 'Holiday Package Deals',
    price: total?.amount,
    index,
  });

  const onClickCTA = (event) => {
    event.preventDefault();

    if (shouldUseNewAnalytics) {
      checkAvailabilityDataLayerEvent();
    }

    if (shouldUseNewEvents) {
      dataLayer.selectItemEvent(selectItemEventData);
      dataLayer.ctaClickEvent({
        itemText: 'Check availability',
        url: href?.pathname,
      });
    }

    const packageOptionAvailable =
      packageOptions.length >= 1 ? packageOptions[0] : undefined;

    if (packageOptionAvailable) {
      push({
        pathname: `/destinations/${tile.propertyContent.destination.name}/properties/${tile.propertyContent.id}/package`,
        query: {
          ...query,
          packageOption: packageOptionAvailable.id,
        },
      });
    } else {
      push(href);
    }
  };

  const onViewPackageDetailsAndTerms = (overrideProps) => (close) => (
    <ViewDetailsModal
      close={close}
      title={`Holiday package to ${propertyContent.destination.title}
                          at ${propertyContent.name}`}
      subtitle={tile.procuredOffer?.name}
      terms={tile.procuredOffer?.terms}
      inclusions={inclusions}
      minNights={nights}
      routes={routes}
      destinationCode={propertyContent.destination.destinationCode}
      travelDateRange={tile.procuredOffer?.travelDateRange}
      {...overrideProps}
    />
  );

  return (
    <>
      <Flex
        data-testid="PROPERTY_OPTION"
        bg="background.card"
        flexDirection={['column', null, 'row']}
        boxShadow="light"
        mt={pillText && 11}
        minHeight={
          {
            [TILE_TYPE.STANDARD_PROPERTY]: '420px',
            [TILE_TYPE.FEATURED_PROPERTY]: '564px',
            [TILE_TYPE.PROCURED_OFFER]: '564px',
          }[tile.type]
        }
        borderRadius="md"
        border="1"
        borderColor="lightGrey"
        overflow="hidden"
      >
        <Box position="absolute">
          {pillText && (
            <StyledPromoPillWrapper>
              <PromoPill
                message={pillText}
                icon={tile.procuredOffer && 'earlyAccess'}
              />
            </StyledPromoPillWrapper>
          )}
        </Box>
        <Box
          height={[
            '200px',
            null,
            {
              [TILE_TYPE.STANDARD_PROPERTY]: '420px',
              [TILE_TYPE.FEATURED_PROPERTY]: '640px',
              [TILE_TYPE.PROCURED_OFFER]: '640px',
            }[tile.type],
          ]}
          width={[null, null, '560px', '660px']}
          position="relative"
        >
          {flightPillText && (
            <Box position="absolute" zIndex="1" right={16} top={16}>
              <Badge
                data-testid="BADGE"
                text={flightPillText + '^'}
                fontWeight="700"
                fontSize="xxs"
                backgroundColor="ui.badgeBackground"
                padding="4px 8px"
              ></Badge>
            </Box>
          )}
          <Skeleton height="100%" lineHeight="unset">
            <ImageGallery
              images={propertyContent.images}
              height={['200px', null, `${height}px`]}
              mediaCounterStyle="counter"
            />
          </Skeleton>
        </Box>

        <Flex ref={sizeRef} flex="1">
          <Flex
            pt={['4', null, '6']}
            pb="6"
            flexDirection="column"
            justifyContent="space-between"
            flex="1"
            height="100%"
          >
            <Box px={['4', null, '6']}>
              <Skeleton width="60px" />
              <Skeleton>
                <Box>
                  <PropertyDestination
                    destinationName={propertyContent.destination.title}
                    iconSize={20}
                    fontSize={'xs'}
                  />
                  <Box mb={4}>
                    <PropertyTitle
                      propertyContent={propertyContent}
                      withDestination={false}
                      withTags
                      procuredOffer={tile.procuredOffer}
                    />
                  </Box>
                  {tile.type === TILE_TYPE.STANDARD_PROPERTY && (
                    <Box mb={4}>
                      <Text data-testid="STANDARD_PROPERTY_DESCRIPTION">
                        {tile.propertyContent.property?.shortDescription}
                      </Text>
                    </Box>
                  )}
                  <Box
                    minHeight={
                      {
                        [TILE_TYPE.FEATURED_PROPERTY]: [null, null, '100px'],
                        [TILE_TYPE.PROCURED_OFFER]: [null, null, '160px'],
                      }[tile.type]
                    }
                  >
                    {hasInclusions ? (
                      <>
                        <DealInclusions
                          inclusions={inclusions}
                          shownInclusions={4}
                          hideSubtitle
                        />
                        <ToggleContent
                          toggle={(show) => (
                            <LinkButton
                              noPadding
                              mb={[2, null, null]}
                              fontSize="sm"
                              onClick={(e) => {
                                e.preventDefault();

                                if (shouldUseNewAnalytics) {
                                  viewPackageDetailsDataLayerEvent();
                                }
                                show();
                              }}
                            >
                              <Text bold fontSize={['xs', null, 'sm']} link>
                                View package details and terms
                              </Text>
                            </LinkButton>
                          )}
                          content={(close) => (
                            <ViewDetailsModal
                              close={close}
                              title={`Holiday package to ${propertyContent.destination.title}
                                      at ${propertyContent.name}`}
                              subtitle={tile.procuredOffer?.name}
                              terms={tile.procuredOffer?.terms}
                              inclusions={inclusions}
                              minNights={nights}
                              routes={routes}
                              destinationCode={
                                propertyContent.destination.destinationCode
                              }
                              travelDateRange={
                                tile.procuredOffer?.travelDateRange
                              }
                            />
                          )}
                        />
                      </>
                    ) : (
                      <ReadMore
                        maxWidth={[null, null, '400px']}
                        showMoreText="Show more"
                        showLessText="Show less"
                        displayIcons
                        lines={3}
                      >
                        {tagline && <Text>{tagline}</Text>}
                      </ReadMore>
                    )}
                  </Box>
                </Box>
              </Skeleton>
            </Box>
            <Hide md lg>
              <Box px={['4', null, '6']} mb={4}>
                <Divider />
              </Box>
            </Hide>
            <Box px={['4', null, '6']} mt={[0, null, 4]}>
              {tile.type !== TILE_TYPE.STANDARD_PROPERTY && (
                <Skeleton width="30px">
                  <PackageHeroSummary showNights nights={nights} exact />
                </Skeleton>
              )}
              {total && travellers && (
                <Skeleton width="100px">
                  <StyledPriceTotal mt="2">
                    {hidePerPersonPrice ? (
                      <PricePerTraveller
                        perTraveller={perTraveller}
                        total={total}
                        travellers={travellers}
                        hidePerPersonPrice={hidePerPersonPrice}
                        valuedAtTotal={valuedAtTotal}
                      />
                    ) : (
                      <PricePerTraveller
                        displayTravellerTypes={displayTravellerTypes}
                        perAdult={perAdult}
                        perTraveller={perTraveller}
                        priceForAdult={priceForAdult}
                        total={total}
                        travellers={travellers}
                        valuedAtTotal={valuedAtTotal}
                      />
                    )}
                  </StyledPriceTotal>
                </Skeleton>
              )}

              {tile.type !== TILE_TYPE.STANDARD_PROPERTY && (
                <Skeleton width="100px">
                  <Box
                    textAlign="right"
                    color="text.body"
                    mt={2}
                    fontSize={['xxs', null, 'sm']}
                  >
                    {`Departing ${departingOrigin ?? origin?.name}`}
                  </Box>
                </Skeleton>
              )}

              <Flex
                flexDirection={['column', null, 'row-reverse']}
                gap="16px"
                mt={4}
              >
                <Flex flex="1 0 0">
                  <Skeleton type="Button" width={['100%', null, '254px']}>
                    <Button
                      onClick={onClickCTA}
                      outline
                      data-testid="CTA"
                      nowrap
                      block={[true, null, shouldShowViewOtherOptions]}
                      width={[
                        0,
                        null,
                        !shouldShowViewOtherOptions ? '254px' : null,
                      ]}
                      marginLeft={[
                        null,
                        null,
                        !shouldShowViewOtherOptions ? 'auto' : null,
                      ]}
                    >
                      Check availability
                    </Button>
                  </Skeleton>
                </Flex>

                {shouldShowViewOtherOptions && (
                  <ToggleContent
                    toggle={(show) => (
                      <Flex flex="1 0 0">
                        <Skeleton type="Button" width={['100%', null, '254px']}>
                          <Button
                            onClick={(event) => {
                              handleViewAllOptions(event, show);
                            }}
                            variant="secondary"
                            data-testid="VIEW_ALL_OPTIONS_CTA"
                            nowrap
                            block
                          >
                            View all options
                          </Button>
                        </Skeleton>
                      </Flex>
                    )}
                    content={(close) => (
                      <AllPackageOptionsModal
                        onClose={close}
                        viewPackageDetailsAndTermsContent={
                          onViewPackageDetailsAndTerms
                        }
                        tile={tile}
                        property={tile.propertyContent}
                        packageOptions={packageOptions}
                      />
                    )}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Box mt={2}>
        <ProcuredOfferFooter
          propertyContent={tile.propertyContent}
          procuredOffer={tile.procuredOffer}
          nights={nights}
          departingOrigin={departingOrigin}
          origin={origin}
          inclusions={inclusions}
        />
      </Box>
    </>
  );
};

PropertyOption.propTypes = {
  tile: PropTypes.shape(Tile).isRequired,
  priceForAdult: PropTypes.bool,
  hidePerPersonPrice: PropTypes.bool,
  index: PropTypes.number.isRequired,
};

const PropertyOptionWithSkeleton = withSkeleton(({ loading }) => loading)(
  PropertyOption,
);

export default PropertyOptionWithSkeleton;
